import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { GlobalSearch } from './GlobalSearch';
import { ChangePasswordDialog } from './ChangePasswordDialog';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  LayoutDashboard, 
  Home, 
  Users, 
  Building2, 
  MapPin, 
  Utensils, 
  MessageCircle, 
  FileText, 
  Settings, 
  LogOut,
  Menu,
  X,
  ChevronDown,
  ChevronRight,
  Bell,
  User,
  UserX,
  Crown,
  List,
  FolderOpen,
  Database,
  Calendar,
  Syringe,
  IndianRupee,
  Ticket,
  Armchair,
  ClipboardList,
  Wallet,
  Lock,
  ClipboardCheck,
  Video,
  Radio
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';

// Family & Location sub-items (merged with Mohalla & Sector management)
const familyLocationItems = [
  { path: '/families', icon: Home, label: 'Families' },
  { path: '/members', icon: Users, label: 'Members' },
  { path: '/non-tagged', icon: UserX, label: 'Non-Tagged', adminOnly: true },
  { path: '/mohalla-sector-management', icon: Database, label: 'Mohalla & Sector', adminOnly: true },
  { path: '/leadership', icon: Crown, label: 'Masool / Musaid', adminOnly: true },
];

// Events sub-items (shown under Events in sidebar)
const eventsSubItems = [
  { path: '/events', icon: Calendar, label: 'Events' },
  { path: '/eid-zabhiyat', icon: Calendar, label: 'Eid ul Adha', adminOnly: true },
];

const mainNavItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard', dashboardOnly: true },

  // ── EVENTS & ATTENDANCE ──
  { type: 'header', label: 'Events & Attendance' },
  { path: '/events', icon: Calendar, label: 'Events' },
  { path: '/token-events', icon: Ticket, label: 'Token Events', tokenAccess: true },
  { path: '/attendance-manage', icon: ClipboardCheck, label: 'Attendance', attendanceAccess: true },

  // ── SERVICES ──
  { type: 'header', label: 'Services' },
  { path: '/fmb', icon: Utensils, label: 'FMB Thaali' },
  { path: '/seat-management', icon: Armchair, label: 'Seat Management', adminOnly: true },
  { path: '/umoor-sehat/vaccination', icon: Syringe, label: 'Umoor Sehat', vaccinationAccess: true },

  // ── FINANCE ──
  { type: 'header', label: 'Finance' },
  { path: '/toloba', icon: IndianRupee, label: 'Toloba' },
  { path: '/wajebaat', icon: Wallet, label: 'Wajebaat', wajebaatAccess: true },

  // ── FORMS & LISTS ──
  { type: 'header', label: 'Forms & Lists' },
  { path: '/forms', icon: ClipboardList, label: 'Forms', formAccess: true },
  { path: '/lists', icon: List, label: 'Lists', listAccess: true },
  { path: '/shnd', icon: FolderOpen, label: 'SHND', shndAccess: true },

  // ── MEDIA & COMMUNICATION ──
  { type: 'header', label: 'Media & Communication' },
  { path: '/live-streaming', icon: Radio, label: 'Live Streaming' },
  { path: '/videos', icon: Video, label: 'Videos', videoAccess: true },
  { path: '/messaging', icon: MessageCircle, label: 'Messaging', adminOnly: true },

  // ── SYSTEM ──
  { type: 'header', label: 'System' },
  { path: '/audit', icon: FileText, label: 'Audit Logs', superAdminOnly: true },
  { path: '/users', icon: Settings, label: 'Users', superAdminOnly: true },
  { path: '/data-maintenance', icon: Database, label: 'Data Maintenance', superAdminOnly: true },
];

export function Layout({ children }) {
  const { user, logout, isAdmin, isSuperAdmin, canViewDashboard, canViewAuditLogs, isMasool, isMusaid, isFMBViewer, isTolobaCollector, isTolobaKG, isListManager, canViewFMB, tolobaAccess, eventAccess, accessibleTabs } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [familyMenuOpen, setFamilyMenuOpen] = useState(true);
  const [eventsMenuOpen, setEventsMenuOpen] = useState(true);
  const [showChangePassword, setShowChangePassword] = useState(false);

  // Check if user is a regular Mumineen (no event access, no toloba access, no leadership)
  const isMumineenOnly = user?.role === 'mumineen' && 
    (!eventAccess || eventAccess.length === 0) && 
    (!tolobaAccess || tolobaAccess.length === 0) &&
    !user?.leadership_role;

  // Check if user has any KG assignment (events or toloba)
  const hasKGAssignment = (eventAccess && eventAccess.length > 0) || (tolobaAccess && tolobaAccess.length > 0);

  // Check if user is Masool/Masoola or Musaid/Musaida (restricted view roles)
  const isRestrictedViewRole = isMasool || isMusaid || isFMBViewer || isTolobaCollector || (hasKGAssignment && !isAdmin);

  // Map paths to accessible_tabs module names
  const pathToModule = {
    '/': 'dashboard',
    '/fmb': 'fmb_thaali',
    '/events': 'events',
    '/eid-zabhiyat': 'events',
    '/token-events': 'token_events',
    '/attendance-manage': 'attendance',
    '/videos': 'videos',
    '/wajebaat': 'wajebaat',
    '/ramzan-seats': 'ramzan_seats',
    '/toloba': 'toloba',
    '/lists': 'lists',
    '/forms': 'forms',
    '/shnd': 'shnd',
    '/messaging': 'messaging',
    '/audit': 'audit_logs',
    '/users': 'users',
    '/families': 'families',
    '/members': 'members',
    '/non-tagged': 'non_tagged',
    '/mohalla-sector-management': 'mohalla_sector',
    '/leadership': 'masool_musaid',
    '/live-streaming': 'live_streaming',
  };

  // Check if user can access a specific path based on accessible_tabs
  const canAccessPath = (path) => {
    const module = pathToModule[path];
    if (!module) return true; // Unknown paths are allowed
    
    // If accessible_tabs is provided by server, use it strictly
    if (accessibleTabs && accessibleTabs.length > 0) {
      return accessibleTabs.includes(module);
    }
    
    // Fallback for when accessibleTabs is not available (shouldn't happen)
    // Only allow admin/super_admin to see all tabs
    if (isAdmin || isSuperAdmin) return true;
    
    // For restricted roles without accessibleTabs, deny by default
    return false;
  };

  // Menu filtering based on accessible_tabs from server
  const filteredMainItems = mainNavItems.filter(item => {
    if (item.type === 'header') return true; // headers are pruned later if their section is empty
    // Hard role gates first
    if (item.superAdminOnly && !isSuperAdmin) return false;
    if (item.adminOnly && !isAdmin) return false;
    if (item.vaccinationAccess) {
      const ok = isAdmin || user?.role === 'vaccination_coordinator'
        || (Array.isArray(user?.roles) && user.roles.includes('vaccination_coordinator'));
      if (!ok) return false;
      return true;
    }
    // Then check if user has access via accessible_tabs
    if (!canAccessPath(item.path)) return false;

    // Special cases for users with specific form/event assignments
    if (item.formAccess && user?.has_form_access) return true;
    if (item.path === '/toloba' && isTolobaKG) return true;

    return true;
  });

  // Prune headers whose entire section is empty
  const prunedMainItems = filteredMainItems.filter((item, idx, arr) => {
    if (item.type !== 'header') return true;
    // Walk forward — keep the header only if at least one non-header item exists before the next header
    for (let j = idx + 1; j < arr.length; j += 1) {
      if (arr[j].type === 'header') return false;
      return true;
    }
    return false;
  });
  
  // Family items - filter based on accessible_tabs
  const filteredFamilyItems = familyLocationItems.filter(item => {
    return canAccessPath(item.path);
  });

  // Check if current path is in family/location section
  const isFamilyLocationActive = familyLocationItems.some(item => location.pathname === item.path);

  const NavLink = ({ item, onClick }) => {
    const isActive = location.pathname === item.path;
    return (
      <Link
        to={item.path}
        onClick={onClick}
        className={`sidebar-link ${isActive ? 'active' : ''}`}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <item.icon className="w-5 h-5" />
        {item.label}
      </Link>
    );
  };

  const SubNavLink = ({ item, onClick }) => {
    const isActive = location.pathname === item.path;
    return (
      <Link
        to={item.path}
        onClick={onClick}
        className={`flex items-center gap-3 px-4 py-2 pl-10 text-sm transition-colors ${
          isActive 
            ? 'bg-navy/20 text-white border-l-2 border-gold' 
            : 'text-slate-400 hover:text-white hover:bg-slate-800/50'
        }`}
        data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      >
        <item.icon className="w-4 h-4" />
        {item.label}
      </Link>
    );
  };

  const SidebarContent = ({ onNavigate }) => (
    <>
      {/* Logo */}
      <div className="p-4 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <img 
            src="/logo.jpeg" 
            alt="Anjuman E Saifee" 
            className="w-12 h-12 rounded-lg object-cover"
          />
          <div>
            <h1 className="font-heading font-bold text-lg leading-tight">Anjuman E Saifee</h1>
            <p className="text-xs text-slate-400">Management System</p>
          </div>
        </div>
      </div>

      {/* Navigation - Now using accessibleTabs from server */}
      <nav className="flex-1 py-4 overflow-y-auto">
        {/* Dashboard - Only if accessible */}
        {canAccessPath('/') && (
          <NavLink item={mainNavItems[0]} onClick={onNavigate} />
        )}

        {/* Family & Location Management - Collapsible, only if any family items accessible */}
        {filteredFamilyItems.length > 0 && (
          <>
            <div
              className="px-5 pt-4 pb-1 text-[10px] uppercase tracking-[0.12em] text-slate-500 font-semibold"
              data-testid="nav-section-people"
            >
              People
            </div>
            <Collapsible open={familyMenuOpen} onOpenChange={setFamilyMenuOpen}>
            <CollapsibleTrigger className={`w-full flex items-center gap-3 px-5 py-3 text-sm font-medium transition-colors ${
              isFamilyLocationActive 
                ? 'bg-navy/30 text-white' 
                : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
            }`}>
              <FolderOpen className="w-5 h-5" />
              <span className="flex-1 text-left">Family & Location</span>
              {familyMenuOpen ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </CollapsibleTrigger>
            <CollapsibleContent className="bg-slate-900/50">
              {filteredFamilyItems.map((item) => (
                <SubNavLink key={item.path} item={item} onClick={onNavigate} />
              ))}
            </CollapsibleContent>
          </Collapsible>
          </>
        )}

        {/* Main navigation items - filtered by accessibleTabs */}
        {prunedMainItems.filter(item => item.path !== '/').map((item, idx) => {
          // Section header divider
          if (item.type === 'header') {
            return (
              <div
                key={`hdr-${idx}-${item.label}`}
                className="px-5 pt-4 pb-1 text-[10px] uppercase tracking-[0.12em] text-slate-500 font-semibold"
                data-testid={`nav-section-${item.label.toLowerCase().replace(/\s+/g, '-').replace(/&/g, 'and')}`}
              >
                {item.label}
              </div>
            );
          }
          // Events: render inline as a Collapsible (with Eid sub-item) or flat link
          if (item.path === '/events') {
            const isEventsActive = ['/events', '/eid-zabhiyat'].includes(location.pathname);
            const subItems = eventsSubItems.filter(sub => {
              if (sub.adminOnly && !isAdmin) return false;
              return true;
            });
            const showCount = eventAccess && eventAccess.length > 0 && !isAdmin;
            if (subItems.length <= 1) {
              return (
                <Link
                  key="events-flat"
                  to="/events"
                  onClick={onNavigate}
                  className={`sidebar-link ${location.pathname === '/events' ? 'active' : ''}`}
                  data-testid="nav-events"
                >
                  <Calendar className="w-5 h-5" />
                  Events
                  {showCount && <Badge className="ml-auto bg-blue-500 text-white text-xs">{eventAccess.length}</Badge>}
                </Link>
              );
            }
            return (
              <Collapsible key="events-collapsible" open={eventsMenuOpen} onOpenChange={setEventsMenuOpen}>
                <CollapsibleTrigger
                  className={`w-full flex items-center gap-3 px-5 py-3 text-sm font-medium transition-colors ${
                    isEventsActive ? 'bg-navy/30 text-white' : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
                  }`}
                  data-testid="nav-events-trigger"
                >
                  <Calendar className="w-5 h-5" />
                  <span className="flex-1 text-left">Events</span>
                  {eventsMenuOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </CollapsibleTrigger>
                <CollapsibleContent className="bg-slate-900/50">
                  {subItems.map((sub) => (
                    <SubNavLink key={sub.path} item={sub} onClick={onNavigate} />
                  ))}
                </CollapsibleContent>
              </Collapsible>
            );
          }
          // Special handling for items that need badges
          if (item.path === '/toloba' && tolobaAccess && tolobaAccess.length > 0) {
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onNavigate}
                className={`sidebar-link ${location.pathname === item.path ? 'active' : ''}`}
                data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
                <Badge className="ml-auto bg-emerald-500 text-white text-xs">{tolobaAccess.length}</Badge>
              </Link>
            );
          }
          return <NavLink key={item.path} item={item} onClick={onNavigate} />;
        })}
      </nav>

      {/* User Info */}
      <div className="p-4 border-t border-slate-800">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-navy rounded-full flex items-center justify-center">
            <User className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.full_name}</p>
            <p className="text-xs text-slate-400 capitalize">
              {user?.leadership_role || (user?.roles?.length > 1 ? `${user.roles.length} roles` : user?.role?.replace('_', ' '))}
            </p>
            {/* Show Mohalla/Sector for leadership roles */}
            {user?.mohalla && (
              <p className="text-xs text-emerald-400 truncate">
                {user.mohalla}{user?.sector ? ` • ${user.sector}` : ''}{user?.sectors?.length > 1 ? ` (+${user.sectors.length - 1})` : ''}
              </p>
            )}
            {/* Show Event-assigned sectors for distribution (KG, Mohalla Head, Incharge) */}
            {!user?.mohalla && user?.event_access?.length > 0 && (
              <p className="text-xs text-amber-400 truncate">
                {user.event_access[0]?.role === 'khidmat_guzar' && user.event_access[0]?.sectors?.length > 0 
                  ? `KG: ${user.event_access[0].sectors.join(', ')}`
                  : user.event_access[0]?.role === 'mohalla_head' && user.event_access[0]?.mohalla
                    ? `MH: ${user.event_access[0].mohalla}`
                    : user.event_access[0]?.role === 'incharge'
                      ? 'Event Incharge'
                      : ''}
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50/50">
      {/* Sidebar - Desktop */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-slate-900 text-white hidden md:flex flex-col z-30" data-testid="sidebar">
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="fixed left-0 top-0 h-full w-64 bg-slate-900 text-white flex flex-col">
            <SidebarContent onNavigate={() => setSidebarOpen(false)} />
          </aside>
        </div>
      )}

      {/* Main Content */}
      <div className="md:ml-64 min-h-screen">
        {/* Top Bar */}
        <header className="sticky top-0 bg-white border-b border-slate-200 z-20">
          <div className="flex items-center justify-between px-4 h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                className="md:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-5 h-5" />
              </Button>
              <GlobalSearch />
            </div>

            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" className="relative">
                <Bell className="w-5 h-5" />
              </Button>

              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-navy rounded-full flex items-center justify-center text-white">
                      <User className="w-4 h-4" />
                    </div>
                    <ChevronDown className="w-4 h-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    className="flex items-center gap-2 cursor-pointer"
                    onClick={() => setShowChangePassword(true)}
                    data-testid="change-password-menu-item"
                  >
                    <Lock className="w-4 h-4" />
                    Change Password
                  </DropdownMenuItem>
                  <DropdownMenuItem className="flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    className="flex items-center gap-2 text-red-600"
                    onClick={logout}
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-3 sm:p-4 md:p-6">
          {children}
        </main>
      </div>
      
      {/* Change Password Dialog */}
      <ChangePasswordDialog 
        open={showChangePassword} 
        onOpenChange={setShowChangePassword} 
      />
    </div>
  );
}
